import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Injector,
  input,
  OnInit
} from '@angular/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';

import { Project } from '@core/models/project';
import { ACCESS_STORE } from '@core/stores/access.store';
import { APP_STORE } from '@core/stores/app.store';
import { USER_STATE_STORE } from '@core/stores/user-state.store';
import { AccountType } from '@shared/models/account-type';
import { BaseAccount } from '@shared/models/base-account';
import { waitForSignalLoaded } from '@shared/utils/wait-for-signal-loaded';
import { ENVIRONMENT } from '@env/environment';

@Component({
  selector: 'app-client-setup',
  templateUrl: './client-setup.component.html',
  styleUrls: ['./client-setup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [MatProgressSpinner]
})
export class ClientSetupComponent implements OnInit {
  readonly clientId = input<string>();
  readonly projectId = input<string>();
  readonly redirectTo = input<string>();

  readonly #injector = inject(Injector);
  readonly #router = inject(Router);
  readonly #appStore = inject(APP_STORE);
  readonly #userStateStore = inject(USER_STATE_STORE);
  readonly #accessStore = inject(ACCESS_STORE);

  readonly #projectsLoaded = computed(
    () => this.#accessStore.projects().loaded
  );
  readonly #userAccountSnippetsLoaded = computed(
    () => this.#accessStore.userAccountSnippets().loaded
  );
  readonly errorOccurred = computed(
    () =>
      !!this.#accessStore.projects().error ||
      !!this.#accessStore.userAccountSnippets().error
  );

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  async ngOnInit(): Promise<void> {
    const clientId = this.clientId();

    if (!clientId) {
      void this.#router.navigate(['/error'], {
        queryParams: {
          errorType: 'no-client'
        }
      });

      return this.#appStore.setupClientSuccess(clientId);
    }

    const selectedAccount = this.#appStore.selectedAccount();

    if (
      selectedAccount?.accountType !== AccountType.SystemAdmin &&
      !selectedAccount?.client?.modules.includes(ENVIRONMENT.moduleId)
    ) {
      void this.#router.navigate(['/error'], {
        queryParams: {
          errorType: 'no-module-access'
        }
      });

      return this.#appStore.setupClientSuccess(clientId);
    }

    const result = await Promise.all([
      this.loadProjects(),
      this.loadUserAccountSnippets()
    ]);

    if (this.errorOccurred()) {
      void this.#router.navigate(['/error'], {
        queryParams: {
          errorType: 'client-setup-failure'
        }
      });

      return this.#appStore.setupClientFailure();
    }

    const projects = result[0];

    if (!projects?.length) {
      void this.#router.navigate(['/error'], {
        queryParams: {
          errorType: 'no-project'
        }
      });

      return this.#appStore.setupClientSuccess(clientId);
    }

    const projectId = this.projectId();
    let redirectTo = this.redirectTo();
    if (projectId && !projects.some(x => x.id === this.projectId())) {
      redirectTo = 'page-not-found';
    }

    const project = this.selectProject(projectId, projects);

    void this.#router.navigate(['/project-setup'], {
      queryParams: {
        clientId,
        projectId: project.id,
        redirectTo
      },
      replaceUrl: true
    });

    this.#appStore.setupClientSuccess(clientId);
  }

  async loadProjects(): Promise<Project[]> {
    this.#accessStore.loadProjects();

    await waitForSignalLoaded(this.#projectsLoaded, this.#injector);

    return this.#accessStore.projects().entities;
  }

  async loadUserAccountSnippets(): Promise<BaseAccount[]> {
    this.#accessStore.loadUserAccountSnippets();

    await waitForSignalLoaded(this.#userAccountSnippetsLoaded, this.#injector);

    return this.#accessStore.userAccountSnippets().entities;
  }

  selectProject(projectId: string, projects: Project[]): Project {
    const project =
      projects.find(x => x.id === projectId) ||
      projects.find(
        x => x.id === this.#userStateStore.data()?.lastSelectedProjectId
      ) ||
      projects[0];

    this.#appStore.selectProject(project?.id);

    return project;
  }
}
